import { ResponsiveContainer, BarChart, XAxis, YAxis, Tooltip, Bar, Cell } from 'recharts';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import type { FOSSAColor } from '../../../theme/colors';
import { prettyNumber } from '../../../../shared/utils';
import { fossaPrimaryFont } from '../../../theme/typography';
import COLORS from '../../../theme/colors';

export type BarData = {
  name: string;
  value: number;
  color: FOSSAColor;
};

type Props = {
  /**
   * The data for each individual bar to show on the graph.
   */
  data: BarData[]
  /**
   * Whether or not to show tooltips when hovering over the graph.
   */
  showTooltips?: boolean;
  yAxisConfig?: {
    width?: number;
  }
};

const HorizontalBarGraph: VoidFunctionComponent<Props> = ({ data, showTooltips, yAxisConfig }) => {
  return (
    // We have to dynamically set the height because the ResponsiveContainer needs a set number and the number of
    // bars affects the needed height, but the height also affects the sizings of the bars.
    // Assigning 40 pixels for each bar was chosen through trial and error, since the sizings were a big hard
    // to predict.
    <ResponsiveContainer height={40 * data.length}>
      <BarChart data={data} layout="vertical" barSize={16} barCategoryGap={0} barGap={4}>
        <XAxis type="number" dataKey="value" hide padding={{ left: 1, right: 50 }} />
        <YAxis
          type="category"
          dataKey="name"
          tickSize={0}
          tickMargin={16}
          width={yAxisConfig?.width}
          axisLine={{
            strokeWidth: 2,
            stroke: COLORS.fossaBlack,
          }}
          tick={{
            fontSize: 12,
            fontFamily: 'Open Sauce Two',
            fontWeight: 500,
            color: COLORS.fossaGray80,
            x: 0,
          }}
        />
        {showTooltips && (
          <Tooltip
            cursor={{ fill: 'transparent' }}
            formatter={(value, name) => [prettyNumber(value as number), _.capitalize(name as string)]}
            labelStyle={{ fontFamily: fossaPrimaryFont, fontWeight: 'bold' }}
            contentStyle={{ fontFamily: fossaPrimaryFont }}
          />
        )}
        <Bar
          dataKey="value"
          background={false}
          minPointSize={4}
          label={{
            position: 'right',
            style: { fontSize: 12, fontWeight: 400, fontFamily: 'iA Writer Quattro', fill: COLORS.fossaGray80 },
          }}
        >
          {data.map((entry) => (
            // @ts-expect-error It doesn't like giving number[] to radius but it's actually okay.
            <Cell key={`cell-${entry.name}`} fill={entry.color} radius={[0, 2, 2, 0]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default HorizontalBarGraph;
